import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { api } from '../../context/AuthContext';
import { useLicense } from '../../context/LicenseContext';
import {
  Shield, Key, Server, Clock, AlertTriangle, CheckCircle2,
  Copy, Loader2, RefreshCw, Wifi, WifiOff, Lock, Unlock
} from 'lucide-react';
import { toast } from 'sonner';

const LicensePage = () => {
  const { licenseStatus, refreshLicense, isActivated, isExpired, isTampered, licenseRemaining, isWarning } = useLicense();
  const [serverInfo, setServerInfo] = useState(null);
  const [licenseKey, setLicenseKey] = useState('');
  const [activating, setActivating] = useState(false);
  const [loadingInfo, setLoadingInfo] = useState(true);

  useEffect(() => {
    fetchServerInfo();
  }, []);

  const fetchServerInfo = async () => {
    try {
      const res = await api.get('/license/server-info');
      setServerInfo(res.data);
    } catch (err) {
      console.error('Failed to fetch server info:', err);
    } finally {
      setLoadingInfo(false);
    }
  };

  const handleActivate = async () => {
    if (!licenseKey.trim()) {
      toast.error('Please paste a license key');
      return;
    }
    setActivating(true);
    try {
      const res = await api.post('/license/activate-v2', { license_key: licenseKey.trim() });
      toast.success(res.data.message);
      setLicenseKey('');
      refreshLicense();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Activation failed';
      toast.error(typeof detail === 'string' ? detail : detail.message || 'Activation failed');
    } finally {
      setActivating(false);
    }
  };

  const copyMac = (mac) => {
    navigator.clipboard.writeText(mac);
    toast.success('MAC address copied!');
  };

  const statusColor = isTampered ? 'rose' : isExpired ? 'rose' : isWarning ? 'amber' : isActivated ? 'emerald' : 'slate';
  const statusIcon = isTampered ? AlertTriangle : isExpired ? Lock : isActivated ? CheckCircle2 : Unlock;
  const StatusIcon = statusIcon;
  const statusText = isTampered ? 'Tampered' : isExpired ? 'Expired' : isActivated ? 'Active' : 'Inactive';

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="license-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">License Management</h1>
          <p className="text-slate-500 dark:text-slate-400">Hardware-locked enterprise license system</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* License Status Card */}
          <Card data-testid="license-status-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className={`h-5 w-5 text-${statusColor}-500`} />
                License Status
              </CardTitle>
              <CardDescription>Current license information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className={`p-5 rounded-xl bg-${statusColor}-50 dark:bg-${statusColor}-900/20 border border-${statusColor}-200 dark:border-${statusColor}-800/50`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2.5 rounded-full bg-${statusColor}-100 dark:bg-${statusColor}-900/50`}>
                    <StatusIcon className={`h-5 w-5 text-${statusColor}-600 dark:text-${statusColor}-400`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className={`text-lg font-semibold text-${statusColor}-700 dark:text-${statusColor}-300`}>
                        {statusText}
                      </p>
                      <Badge variant={isActivated && !isExpired ? 'default' : 'destructive'} data-testid="license-status-badge">
                        {licenseStatus?.status || 'unknown'}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {licenseStatus?.message || 'No license information available'}
                    </p>
                  </div>
                </div>
              </div>

              {isActivated && !isExpired && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="h-4 w-4 text-blue-500" />
                      <span className="text-xs text-slate-500">Days Remaining</span>
                    </div>
                    <p className={`text-2xl font-bold ${licenseRemaining <= 7 ? 'text-amber-600' : 'text-blue-600'}`} data-testid="license-days-remaining">
                      {licenseRemaining}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="h-4 w-4 text-emerald-500" />
                      <span className="text-xs text-slate-500">School</span>
                    </div>
                    <p className="text-lg font-semibold text-slate-700 dark:text-slate-300" data-testid="license-school">
                      {licenseStatus?.school || '-'}
                    </p>
                  </div>
                </div>
              )}

              {licenseStatus?.activation_info && (
                <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Activated</span>
                    <span className="text-slate-700 dark:text-slate-300">
                      {licenseStatus.activation_info.activated_at ? new Date(licenseStatus.activation_info.activated_at).toLocaleDateString() : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Expires</span>
                    <span className="text-slate-700 dark:text-slate-300">
                      {licenseStatus.activation_info.expires_at ? new Date(licenseStatus.activation_info.expires_at).toLocaleDateString() : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Runtime</span>
                    <span className="text-slate-700 dark:text-slate-300">
                      {licenseStatus.activation_info.cumulative_hours || 0} hours
                    </span>
                  </div>
                </div>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={() => refreshLicense()}
                className="w-full"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Status
              </Button>
            </CardContent>
          </Card>

          {/* Server Hardware Info */}
          <Card data-testid="server-info-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5 text-violet-500" />
                Server Hardware Info
              </CardTitle>
              <CardDescription>Hardware identifiers for license generation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingInfo ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-violet-500" />
                </div>
              ) : serverInfo ? (
                <>
                  <div className="p-4 rounded-xl bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800/50">
                    <p className="text-xs text-violet-600 dark:text-violet-400 font-medium mb-2">SERVER MAC ADDRESS(ES)</p>
                    {serverInfo.mac_addresses?.length > 0 ? (
                      serverInfo.mac_addresses.map((mac, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-violet-100 dark:border-violet-800/30 last:border-0">
                          <code className="text-lg font-mono font-bold text-violet-700 dark:text-violet-300" data-testid={`server-mac-${i}`}>
                            {mac}
                          </code>
                          <Button variant="ghost" size="sm" onClick={() => copyMac(mac)} className="text-violet-600">
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500">No MAC addresses detected</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                      <span className="text-slate-500">Hostname</span>
                      <p className="font-medium text-slate-700 dark:text-slate-300 truncate">{serverInfo.hostname}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                      <span className="text-slate-500">License System</span>
                      <p className="font-medium text-slate-700 dark:text-slate-300">
                        {serverInfo.license_system === 'v2_rsa' ? 'RSA-4096' : 'Legacy'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    {serverInfo.public_key_installed ? (
                      <><CheckCircle2 className="h-4 w-4 text-emerald-500" /><span className="text-emerald-600">Public key installed</span></>
                    ) : (
                      <><AlertTriangle className="h-4 w-4 text-amber-500" /><span className="text-amber-600">Public key NOT installed - copy vocalab_public.pem to backend/</span></>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-sm text-slate-500">Failed to load server info</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* License Activation Card */}
        <Card data-testid="license-activate-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-cyan-500" />
              Activate License
            </CardTitle>
            <CardDescription>
              Paste the license key generated from the offline License Generator tool
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">License Key</label>
              <textarea
                data-testid="license-key-input"
                value={licenseKey}
                onChange={(e) => setLicenseKey(e.target.value)}
                placeholder="Paste your license key here..."
                rows={4}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div className="flex items-center gap-3">
              <Button
                data-testid="activate-license-btn"
                onClick={handleActivate}
                disabled={activating || !licenseKey.trim()}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
              >
                {activating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Activating...</>
                ) : (
                  <><Key className="h-4 w-4 mr-2" /> Activate License</>
                )}
              </Button>
              {isActivated && !isExpired && (
                <p className="text-sm text-emerald-600 flex items-center gap-1">
                  <CheckCircle2 className="h-4 w-4" /> License already active
                </p>
              )}
            </div>

            <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 text-sm text-blue-700 dark:text-blue-300">
              <p className="font-medium mb-2">How to generate a license key:</p>
              <ol className="list-decimal list-inside space-y-1 text-blue-600 dark:text-blue-400">
                <li>Copy the server MAC address from above</li>
                <li>On your PC, run: <code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded">python generate_license.py --mac MAC_ADDRESS --days 365</code></li>
                <li>Copy the generated .key file contents</li>
                <li>Paste above and click Activate</li>
              </ol>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default LicensePage;
